import axios from "axios";
import { APIClient } from "../helpers/api_helper";
import {
  ApiResponse,
  PaginatedApiResponse,
  PaginationParams,
} from "../types/api";

const api = new APIClient();
const RESOURCE = "/support/tickets";

export type SupportTicketCategory =
  | "agenda"
  | "orders"
  | "payments"
  | "account"
  | "integration"
  | "product_feedback"
  | "other";

export type SupportTicketImpact = "blocks" | "annoyance" | "nice_to_have";

export type SupportTicketStatus =
  | "received"
  | "triaging"
  | "in_progress"
  | "awaiting_tenant"
  | "resolved"
  | "closed";

export interface SupportTicketAttachment {
  id: number;
  original_filename: string;
  mime_type: string;
  size_bytes: number;
  created_at: string | null;
}

export interface SupportTicketMessage {
  id: number;
  author_type: "tenant_user" | "platform_agent" | "system";
  body: string;
  is_internal_note: boolean;
  created_at: string | null;
  author_user?: { id: number; name: string } | null;
  author_super_admin?: { id: number; name: string } | null;
  attachments?: SupportTicketAttachment[];
}

export interface SupportTicket {
  id: number;
  public_number: string;
  category: SupportTicketCategory;
  subject: string;
  description: string;
  impact: SupportTicketImpact | null;
  status: SupportTicketStatus;
  source: string;
  page_url: string | null;
  created_at: string | null;
  updated_at: string | null;
  first_response_at: string | null;
  resolved_at: string | null;
  satisfaction?: "yes" | "no" | null;
  satisfaction_comment?: string | null;
  satisfaction_at?: string | null;
  last_agent_message_at?: string | null;
  tenant?: { id: number; name: string } | null;
  assigned_to?: { id: number; name: string; email: string } | null;
  assigned_to_super_admin_id?: number | null;
  snoozed_until?: string | null;
  messages?: SupportTicketMessage[];
  attachments?: SupportTicketAttachment[];
  attachments_count?: number;
}

export interface CreateSupportTicketPayload {
  category: SupportTicketCategory;
  subject: string;
  description: string;
  impact?: SupportTicketImpact;
  page_url?: string;
  attachments?: File[];
}

export interface SupportTicketListParams extends PaginationParams {
  status?: SupportTicketStatus | "";
  category?: SupportTicketCategory | "";
  search?: string;
}

export interface SupportTicketSummary {
  awaiting_tenant: number;
}

export const listSupportTickets = async (
  params?: SupportTicketListParams
): Promise<PaginatedApiResponse<SupportTicket>> => {
  return api.get(RESOURCE, params) as Promise<PaginatedApiResponse<SupportTicket>>;
};

export const getSupportTicketSummary = async (): Promise<
  ApiResponse<SupportTicketSummary>
> => {
  return api.get(`${RESOURCE}/summary`) as Promise<ApiResponse<SupportTicketSummary>>;
};

export const getRecentImprovements = async (
  limit = 5
): Promise<ApiResponse<SupportTicket[]>> => {
  return api.get("/support/improvements/recent", { limit }) as Promise<
    ApiResponse<SupportTicket[]>
  >;
};

export const getSupportTicket = async (
  id: number
): Promise<ApiResponse<SupportTicket>> => {
  return api.get(`${RESOURCE}/${id}`) as Promise<ApiResponse<SupportTicket>>;
};

export const fetchSupportAttachmentBlob = async (
  ticketId: number,
  attachmentId: number
): Promise<Blob> => {
  const blob = await axios.get(
    `${RESOURCE}/${ticketId}/attachments/${attachmentId}/download`,
    { responseType: "blob" }
  );

  return blob as unknown as Blob;
};

export const downloadSupportAttachment = async (
  ticketId: number,
  attachmentId: number,
  filename: string
): Promise<void> => {
  const blob = await fetchSupportAttachmentBlob(ticketId, attachmentId);
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};

export const createSupportTicket = async (
  payload: CreateSupportTicketPayload
): Promise<ApiResponse<{ id: number; public_number: string; status: string }>> => {
  const formData = new FormData();
  formData.append("category", payload.category);
  formData.append("subject", payload.subject);
  formData.append("description", payload.description);

  if (payload.impact) {
    formData.append("impact", payload.impact);
  }

  if (payload.page_url) {
    formData.append("page_url", payload.page_url);
  }

  (payload.attachments || []).forEach((file) => {
    formData.append("attachments[]", file);
  });

  return axios.post(`${RESOURCE}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }) as Promise<ApiResponse<{ id: number; public_number: string; status: string }>>;
};

export const replySupportTicket = async (
  id: number,
  body: string,
  attachments: File[] = []
): Promise<ApiResponse<SupportTicketMessage>> => {
  const formData = new FormData();
  formData.append("body", body);
  attachments.forEach((file) => {
    formData.append("attachments[]", file);
  });

  return axios.post(`${RESOURCE}/${id}/messages`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  }) as Promise<ApiResponse<SupportTicketMessage>>;
};

export const submitSupportSatisfaction = async (
  id: number,
  payload: { satisfaction: "yes" | "no"; comment?: string }
): Promise<ApiResponse<SupportTicket>> => {
  return api.create(`${RESOURCE}/${id}/satisfaction`, payload) as Promise<
    ApiResponse<SupportTicket>
  >;
};
